import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOrderbookTabsKeyboard } from "./useOrderbookTabsKeyboard";
import usePersistentState from "../../../hooks/usePersistentState";
import { getContractPriceStep } from "../../../utils/instruments";
import { countDecimalPlaces } from "../../../utils/math";
import LayoutDropdown from "./LayoutDropdown";
import FilterComponent from "./FilterComponent";
import OrderbookSection from "./OrderbookSection";
import TradeHistorySection from "./TradeHistorySection";
import { OrderbookFilterEnum, OrderbookLayoutEnum } from "./shared";
import {
  ITradeOrderbookSectionProps,
  OrderbookTradeHistoryType,
} from "./types";
import {
  Container,
  LargeContainer,
  LargeOrderbookColumn,
  LargeTradesColumn,
  MediumFilterContainer,
  Section,
  SectionHeader,
  SectionHeaderTitle,
  StackedContainer,
  StackedOrderbookWrapper,
  StackedTradesSeparator,
  StackedTradesWrapper,
  Title,
  TradeTab,
  TradeTabWrapper,
} from "./style";

function TradeOrderbookSection({
  onOrderbookRowClick,
  perpInstrument,
  layout: layoutProp,
  onLayoutChange,
}: ITradeOrderbookSectionProps) {
  // MEDIUM SCREEN ONLY
  const [orderbookTradeHistoryType, setOrderbookTradeHistoryType] = useState(
    OrderbookTradeHistoryType.ORDERBOOK,
  );

  const { orderbookViewInUSD, setOrderbookViewInUSD } = usePersistentState();
  const [orderbookFilter] = useState<OrderbookFilterEnum>();
  const { t } = useTranslation("app", {
    keyPrefix: "Perps.TradeOrderbookSection.TradeOrderbookSection",
  });
  const [orderbookTickSize, setOrderbookTickSize] = useState<number>();
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [tickSizeDropdownOpen, setTickSizeDropdownOpen] = useState(false);
  const [, setLayoutDropdownOpen] = useState(false);
  const layout = layoutProp ?? OrderbookLayoutEnum.TAB;
  const setOrderbookLayout = onLayoutChange ?? (() => {});

  // [ / ] switch the Orderbook/Trades sub-tab while hovering this panel.
  const obGateRef = useOrderbookTabsKeyboard(
    layout,
    orderbookTradeHistoryType,
    setOrderbookTradeHistoryType,
  );

  const availableTickSizes = useMemo(() => {
    if (perpInstrument) {
      const priceStep = Number(perpInstrument.price_step);
      const decimals = countDecimalPlaces(priceStep);

      return [
        undefined,
        Number((priceStep * 10).toFixed(decimals)),
        Number((priceStep * 100).toFixed(decimals)),
        Number((priceStep * 1000).toFixed(decimals)),
      ];
    }
    return [];
  }, [perpInstrument]);

  // Update default OB tick sizes
  useEffect(() => {
    if (availableTickSizes.length) {
      setOrderbookTickSize(availableTickSizes[0]);
    } else {
      setOrderbookTickSize(undefined);
    }
  }, [availableTickSizes]);

  const toggleFilterDropdown = useCallback(() => {
    setFilterDropdownOpen((open) => !open);
  }, []);

  const toggleTickSizeDropdown = useCallback(() => {
    setTickSizeDropdownOpen((open) => !open);
  }, []);

  const toggleLayoutDropdown = useCallback(() => {
    setLayoutDropdownOpen((open) => !open);
  }, []);

  const sizeDecimals = getContractPriceStep(perpInstrument).amount_precision;
  const priceDecimals = getContractPriceStep(perpInstrument).price_precision;

  const filterComponent = useMemo(
    () => (
      <FilterComponent
        toggleTickSizeDropdown={toggleTickSizeDropdown}
        orderbookTickSize={orderbookTickSize}
        perpInstrument={perpInstrument}
        tickSizeDropdownOpen={tickSizeDropdownOpen}
        availableTickSizes={availableTickSizes}
        setOrderbookTickSize={setOrderbookTickSize}
        toggleFilterDropdown={toggleFilterDropdown}
        orderbookViewInUSD={orderbookViewInUSD}
        filterDropdownOpen={filterDropdownOpen}
        setOrderbookViewInUSD={setOrderbookViewInUSD}
      />
    ),
    [
      toggleFilterDropdown,
      orderbookViewInUSD,
      perpInstrument?.underlying_asset,
      perpInstrument?.price_step,
      filterDropdownOpen,
      orderbookFilter,
      setOrderbookViewInUSD,
      orderbookTickSize,
      availableTickSizes,
      tickSizeDropdownOpen,
      toggleTickSizeDropdown,
    ],
  );

  const layoutDropdown = (
    <LayoutDropdown
      layout={layout}
      setLayout={setOrderbookLayout}
      onToggle={toggleLayoutDropdown}
    />
  );

  const orderbookContent = (
    <>
      <MediumFilterContainer>{filterComponent}</MediumFilterContainer>
      <OrderbookSection
        sizeDecimals={sizeDecimals}
        priceDecimals={priceDecimals}
        instrumentName={perpInstrument?.instrument_name}
        onOrderbookRowClick={onOrderbookRowClick}
        filter={orderbookFilter}
        showValueInUSD={orderbookViewInUSD}
        orderbookTickSize={orderbookTickSize}
      />
    </>
  );

  const tradesContent = (
    <TradeHistorySection
      instrumentName={perpInstrument?.instrument_name}
      sizeDecimals={sizeDecimals}
      priceDecimals={priceDecimals}
    />
  );

  if (layout === OrderbookLayoutEnum.STACKED) {
    return (
      <Container>
        <Section>
          <SectionHeader>
            <SectionHeaderTitle>Order Book</SectionHeaderTitle>
            {layoutDropdown}
          </SectionHeader>
          <StackedContainer>
            <StackedOrderbookWrapper>
              {orderbookContent}
            </StackedOrderbookWrapper>
            <StackedTradesSeparator>
              <SectionHeaderTitle>Trades</SectionHeaderTitle>
            </StackedTradesSeparator>
            <StackedTradesWrapper>{tradesContent}</StackedTradesWrapper>
          </StackedContainer>
        </Section>
      </Container>
    );
  }

  if (layout === OrderbookLayoutEnum.LARGE) {
    return (
      <Container>
        <Section style={{ flexDirection: "row" }}>
          <LargeContainer>
            <LargeOrderbookColumn>
              <SectionHeader>
                <SectionHeaderTitle>Order Book</SectionHeaderTitle>
              </SectionHeader>
              {orderbookContent}
            </LargeOrderbookColumn>
            <LargeTradesColumn>
              <SectionHeader>
                <SectionHeaderTitle>Trades</SectionHeaderTitle>
                {layoutDropdown}
              </SectionHeader>
              {tradesContent}
            </LargeTradesColumn>
          </LargeContainer>
        </Section>
      </Container>
    );
  }

  // TAB layout (default)
  return (
    <Container ref={obGateRef}>
      <Section>
        <Title>
          <TradeTabWrapper>
            <TradeTab
              active={
                orderbookTradeHistoryType ===
                OrderbookTradeHistoryType.ORDERBOOK
              }
              onClick={() =>
                setOrderbookTradeHistoryType(
                  OrderbookTradeHistoryType.ORDERBOOK,
                )
              }
            >
              {t(OrderbookTradeHistoryType.ORDERBOOK)}
            </TradeTab>
            <TradeTab
              active={
                orderbookTradeHistoryType === OrderbookTradeHistoryType.TRADES
              }
              onClick={() =>
                setOrderbookTradeHistoryType(OrderbookTradeHistoryType.TRADES)
              }
            >
              {t(OrderbookTradeHistoryType.TRADES)}
            </TradeTab>
          </TradeTabWrapper>
          {layoutDropdown}
        </Title>
        {orderbookTradeHistoryType === OrderbookTradeHistoryType.ORDERBOOK ? (
          <>
            <MediumFilterContainer>{filterComponent}</MediumFilterContainer>
            <OrderbookSection
              sizeDecimals={sizeDecimals}
              priceDecimals={priceDecimals}
              instrumentName={perpInstrument?.instrument_name}
              onOrderbookRowClick={onOrderbookRowClick}
              filter={orderbookFilter}
              showValueInUSD={orderbookViewInUSD}
              orderbookTickSize={orderbookTickSize}
            />
          </>
        ) : (
          <TradeHistorySection
            instrumentName={perpInstrument?.instrument_name}
            sizeDecimals={sizeDecimals}
            priceDecimals={priceDecimals}
          />
        )}
      </Section>
    </Container>
  );
}

export default TradeOrderbookSection;
